import { Box, Grid, HStack, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { EmptyState, IconButton } from 'Atoms';
import { Loader, Select } from 'Molecules';
import { NothingFoundIllustration, Typography } from 'Tokens';
import { EditIcon, RefreshIcon } from 'Tokens/Icons/Function';
import { useMemo, useState } from 'react';
import { useTranslation } from 'utils/translation';
import { MATERIAL_TOPIC_FILTER_OPTIONS } from '../Assessment/EsrsOverview';
import { useGetGroupStandardsOverview } from './GroupAssessmentOverview.hooks';
import {
  AssessmentProgress,
  GroupStandardProgress,
} from '../Assessment/EsrsOverview/StandardProgress';
import { ResponsibleSelector, ToolbarToast } from 'Molecules/TaskToolbar';
import {
  InfoCard,
  MetricInfoCard,
} from '../DisclosureRequirements/StandardPage/StandardPageComponents/DisclosureRequirementHeader';
import {
  EsrsAssessmentDocument_,
  EsrsAssessmentFieldsFragment_,
  useUpdateAssessmentCompanyProjectLeaderMutation,
} from 'models';
import { UserAvatar } from 'Organisms';
export const GroupTopicsOverview = ({
  esrsAssessmentId,
  projectLeader,
  setIsToastLoading,
  setToastText,
  setToastVisible,
}: {
  esrsAssessmentId: string;
  projectLeader: EsrsAssessmentFieldsFragment_['projectLeader'];
  setIsToastLoading: (val: boolean) => void;
  setToastText: (val: string) => void;
  setToastVisible: (val: boolean) => void;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [filter, setFilter] = useState('all');

  const { categories, loading } = useGetGroupStandardsOverview(filter, esrsAssessmentId);

  const [isOwnerHovered, setIsOwnerHovered] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [updateProjectLeader] = useUpdateAssessmentCompanyProjectLeaderMutation();

  const handleUpdateProjectLeader = (projectLeaderId: string, ownerName?: string) => {
    setIsToastLoading(true);
    setToastText(`Assigning responsible...`);
    setToastVisible(true);
    updateProjectLeader({
      variables: { id: esrsAssessmentId, projectLeaderId },
      refetchQueries: [EsrsAssessmentDocument_],
    }).then(() => {
      setIsToastLoading(false);
      setToastVisible(false);
      setToastText(`Assigned ${ownerName} as responsible`);
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 3000);
    });
  };

  const isEmptyFilter = useMemo(
    () => categories.filter((c) => c.materialStandards.length).length === 0,
    [categories]
  );

  const hasNonMaterialStandards = useMemo(
    () =>
      categories.some((c) =>
        c.materialStandards.some((std) => !std.isMaterialForGroup && std.isMaterialForSubsidiary)
      ),
    [categories]
  );

  const filteredCategories = useMemo(() => {
    return categories.map((cat) => ({
      ...cat,
      materialStandards: cat.materialStandards.filter((std) => {
        if (filter === 'all') return true;
        if (filter === 'not-material') {
          return !std.isMaterialForGroup && std.isMaterialForSubsidiary;
        } else if (filter === 'material') {
          return std.isMaterialForGroup;
        }
      }),
    }));
  }, [categories, filter]);

  if (loading) return <Loader />;

  return (
    <VStack alignItems="start" spacing="24px" mt="24px" flexGrow="1">
      {hasNonMaterialStandards && (
        <Box width="280px">
          <Select<{ value: string; label: string }>
            value={{
              label: MATERIAL_TOPIC_FILTER_OPTIONS.find((v) => v.value === filter)?.label ?? '',
              value: filter,
            }}
            onChange={(value) => setFilter(value?.value ? value.value : 'all')}
            options={MATERIAL_TOPIC_FILTER_OPTIONS.map((currOption) => {
              return {
                label: currOption?.label ?? '',
                value: currOption.value,
              };
            })}
            size="md"
          />
        </Box>
      )}

      {isEmptyFilter && (
        <Box w="100%" flexGrow="1">
          <EmptyState
            title={t('common:search.filter.emptyTitle')}
            description={t('common:search.filter.emptyDescription')}
            icon={<NothingFoundIllustration boxSize="120px" />}
            callToAction={{
              text: t('common:search.filter.emptyBtn'),
              variant: 'secondary',
              onClick: () => {
                setFilter('all');
              },
              leftIcon: <RefreshIcon color="inherit" />,
            }}
            component={true}
          />
        </Box>
      )}
      <HStack w="100%" gap="8px" align="stretch">
        <InfoCard title="Data collection progress" content={<AssessmentProgress isGroup />} />
        <MetricInfoCard
          title="Main responsible"
          onMouseEnter={() => setIsOwnerHovered(true)}
          onMouseLeave={() => setIsOwnerHovered(false)}
          content={
            <HStack spacing="8px">
              <UserAvatar
                user={projectLeader ?? undefined}
                size="sm"
                boxSize="28px"
                border="none"
              />
              <Typography variant="h3">{projectLeader?.displayName}</Typography>
              {isOwnerHovered && (
                <IconButton
                  aria-label="edit-dr-owner"
                  icon={<EditIcon />}
                  borderRadius="50%"
                  variant="ghost"
                  onClick={onOpen}
                  size="sm"
                />
              )}
            </HStack>
          }
        />
      </HStack>
      <ResponsibleSelector
        isOpen={isOpen}
        onClose={onClose}
        onSelect={handleUpdateProjectLeader}
        isStandalone
      />
      <VStack alignItems="start" spacing="40px" flexGrow="1" w="100%">
        {filteredCategories.map((category, categoryIndex) => (
          <>
            {category.materialStandards.length > 0 && (
              <VStack key={category.categoryRef} alignItems="start" width="100%">
                <Typography variant="overline">{category.categoryName}</Typography>
                <Grid templateColumns="repeat(3, 1fr)" overflow="visible" gap="12px" w="full">
                  {category.materialStandards.map((standard, standardIndex) => (
                    <GroupStandardProgress
                      standard={standard}
                      standardIndex={standardIndex}
                      categoryIndex={categoryIndex}
                      drRefs={standard.drRefs}
                    />
                  ))}
                </Grid>
              </VStack>
            )}
          </>
        ))}
      </VStack>
    </VStack>
  );
};
